import { useState, useEffect, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import asset from "../lib/asset";

var RESULTS = [
  {
    src: asset("/images/results/case-1.jpg"),
    alt: "До и после: комплексная реабилитация, 4 имплантата на верхней челюсти (2 — Zygoma) и 4 на нижней",
  },
  {
    src: asset("/images/results/case-2.jpg"),
    alt: "До и после: восстановление зубов по методике «Все-на-4» с опорой на имплантаты Straumann",
  },
  {
    src: asset("/images/results/case-3.jpg"),
    alt: "До и после: реабилитация обеих челюстей по методу «Все-на-4»",
  },
  {
    src: asset("/images/results/case-4.jpg"),
    alt: "До и после: реабилитация с опорой на имплантаты Zygoma и Osstem после ношения съёмного протеза",
  },
  { src: asset("/images/results/pair-1.jpg"), alt: "Пациент до имплантации зубов и после" },
  { src: asset("/images/results/pair-2.jpg"), alt: "Пациент до имплантации зубов и после" },
  { src: asset("/images/results/pair-3.jpg"), alt: "Пациент до имплантации зубов и после" },
  { src: asset("/images/results/pair-4.jpg"), alt: "Пациентка до имплантации зубов и после" },
];

export default function ResultsSection() {
  var trackRef = useRef(null);
  var [openIndex, setOpenIndex] = useState(null);
  var isOpen = openIndex !== null;

  var close = useCallback(function () {
    setOpenIndex(null);
  }, []);

  var step = useCallback(function (delta) {
    setOpenIndex(function (prev) {
      if (prev === null) return prev;
      return (prev + delta + RESULTS.length) % RESULTS.length;
    });
  }, []);

  // Прокрутка карусели на одну карточку
  var scrollByCard = function (delta) {
    var track = trackRef.current;
    if (!track) return;
    var card = track.firstElementChild;
    var gap = 16;
    var shift = card ? card.offsetWidth + gap : track.clientWidth;
    track.scrollBy({ left: shift * delta, behavior: "smooth" });
  };

  useEffect(
    function () {
      if (!isOpen) return undefined;

      var onKeyDown = function (e) {
        if (e.key === "Escape") close();
        if (e.key === "ArrowRight") step(1);
        if (e.key === "ArrowLeft") step(-1);
      };

      var prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", onKeyDown);

      return function () {
        document.body.style.overflow = prevOverflow;
        document.removeEventListener("keydown", onKeyDown);
      };
    },
    [isOpen, close, step]
  );

  return (
    <section data-testid="results-section" className="py-12 md:py-16 bg-white">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <h2 data-testid="results-title" className="font-heading text-2xl md:text-3xl font-bold">
              Результаты наших пациентов
            </h2>
            <p className="text-base text-slate-500 mt-1.5">До и после лечения</p>
          </div>

          <div className="hidden md:flex gap-2 flex-shrink-0">
            <button
              type="button"
              data-testid="results-prev"
              onClick={function () { scrollByCard(-1); }}
              aria-label="Предыдущие работы"
              className="w-11 h-11 rounded-full border border-slate-200 hover:border-brand-400 hover:bg-brand-50 text-slate-600 flex items-center justify-center transition-colors cursor-pointer"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              data-testid="results-next"
              onClick={function () { scrollByCard(1); }}
              aria-label="Следующие работы"
              className="w-11 h-11 rounded-full border border-slate-200 hover:border-brand-400 hover:bg-brand-50 text-slate-600 flex items-center justify-center transition-colors cursor-pointer"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div
          ref={trackRef}
          data-testid="results-track"
          className="no-scrollbar flex gap-4 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0 pb-1"
        >
          {RESULTS.map(function (item, index) {
            return (
              <button
                key={item.src}
                type="button"
                data-testid={"result-card-" + index}
                onClick={function () { setOpenIndex(index); }}
                aria-label="Открыть фотографию крупнее"
                className="snap-start flex-shrink-0 basis-[78%] sm:basis-[46%] lg:basis-[31%] xl:basis-[23.5%] rounded-2xl overflow-hidden bg-black border border-slate-200 cursor-pointer focus:outline-none focus:ring-4 focus:ring-brand-600/30"
              >
                <img
                  src={item.src}
                  alt={item.alt}
                  loading="lazy"
                  width="1000"
                  height="1000"
                  className="w-full aspect-square object-cover"
                />
              </button>
            );
          })}
        </div>

        <p className="text-xs text-slate-400 leading-relaxed mt-4">
          Фотографии публикуются с согласия пациентов. Результат лечения индивидуален.
        </p>
      </div>

      {isOpen && (
        <div
          data-testid="results-lightbox"
          role="dialog"
          aria-modal="true"
          onClick={close}
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 animate-fade-in"
        >
          <button
            type="button"
            onClick={close}
            aria-label="Закрыть"
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
          >
            <X className="w-6 h-6" />
          </button>

          <button
            type="button"
            onClick={function (e) { e.stopPropagation(); step(-1); }}
            aria-label="Предыдущее фото"
            className="absolute left-2 md:left-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            type="button"
            onClick={function (e) { e.stopPropagation(); step(1); }}
            aria-label="Следующее фото"
            className="absolute right-2 md:right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <img
            onClick={function (e) { e.stopPropagation(); }}
            src={RESULTS[openIndex].src}
            alt={RESULTS[openIndex].alt}
            className="max-w-3xl w-full max-h-[85vh] object-contain rounded-2xl"
          />
        </div>
      )}
    </section>
  );
}
